import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDownLeft, ArrowUpRight, ArrowRight, Search, RotateCcw, User, X, Check, Eye, Upload} from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import { getUserlist } from "../../services/api";
import { approvePendingReward, rejectPendingReward } from "../../store/ledgerSlices/rewardLedgerActionSlice";

const PER_PAGE = 50;
const BILL_MAX_BYTES = 2 * 1024 * 1024;
const BILL_TYPES = ["image/png", "image/jpeg", "application/pdf"];
/* ─────────────────────────────────────────────────────────────────────────────
   API shape lives in ONE place. When the response changes, only edit
   normalizeLedgerRow() (row fields) and buildPayload() (request body).
   ───────────────────────────────────────────────────────────────────────────── */
export const normalizeLedgerRow = (r) => {
    const typeCode = String(r.type ?? r.customer_transaction_type ?? "").toUpperCase();
    const isCredit = typeof r.is_credit === "boolean" ? r.is_credit : typeCode === "CR";
    return {
        id: r.id,
        createdAt: r.created_at,
        referenceNo: r.ReferenceNo,
        description: r.description,
        customerName: r.name,
        customerEmail: r.email,
        customerMobile: r.mobile ? `${r.countrycode ? "+" + r.countrycode + " " : ""}${r.mobile}` : "",
        from: r.from_name,
        to: r.to_name,
        mode: r.RewardMode,
        status: (() => {
        const status = String(r.status ?? "").toUpperCase();
        return status === "FAILED" ? "REJECTED" : status;
        })(),
        isCredit,
        amount: Math.abs(Number(r.points ?? r.amount ?? 0)), // points = this entry; API "amount" is 0 on the normal ledger
        balance: r.balance_points ?? r.running_balance, // balance_points = balance after this entry; running_balance is a constant 100 here
        order_amount:r.order_amount,
        approvedAt: r.approved_at,
        approvedBy: r.is_store_approved === true || Number(r.is_store_approved) === 1 ? "Store" : "Admin",
        billUrl: r.uploaded_bill || null,
    };
};

// Customer search result (same /customer/fetch API the Customer page uses)
const normalizeCustomer = (c) => ({
    id: c.id,
    name: (c.name || `${c.fname || ""} ${c.lname || ""}`).trim() || "Customer",
    email: c.email || "",
    mobile: c.mobile || "",
});

const buildPayload = (f) => ({
    customer_id: Number(f.customerId) || 0,
    bookingID: (f.bookingId || "").trim(),
    FromDate: f.fromDate ? dayjs(f.fromDate).startOf("day").toISOString() : null,
    ToDate: f.toDate ? dayjs(f.toDate).endOf("day").toISOString() : null,
    mode: f.mode || null,
});

const defaultFilters = () => ({
    fromDate: dayjs().subtract(6, "month").format("YYYY-MM-DD"),
    toDate: dayjs().format("YYYY-MM-DD"),
    customerId: "",
    bookingId: "",
    mode: "",
});

const MODE_OPTIONS = ["Earn", "Pay", "Refund"];

/* Static class maps so Tailwind can see every class name */
const TONE = {
    credit: {
        edge: "border-l-4 border-green-500",
        badge: "bg-green-50 text-green-700 border border-green-200",
        amount: "text-green-600",
        label: "Credit",
        sign: "+",
    },
    debit: {
        edge: "border-l-4 border-red-500",
        badge: "bg-red-50 text-red-700 border border-red-200",
        amount: "text-red-600",
        label: "Debit",
        sign: "-",
    },
};

const STATUS_STYLE = {
    SUCCESS: "bg-green-50 text-green-700 border border-green-200",
    PENDING: "bg-amber-50 text-amber-700 border border-amber-200",
    FAILED: "bg-red-50 text-red-700 border border-red-200",
};
const STATUS_FALLBACK = "bg-gray-100 text-gray-700 border border-gray-200";

const money = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (v) => (v === null || v === undefined || v === "" || isNaN(Number(v)) ? "-" : money.format(Number(v)));

const inputCls = "w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none";

export default function RewardLedgerView({ title, subtitle, stateKey, fetchAction, emptyText = "No ledger entries found" }) {
    useDocumentTitle(`Welcome to Admin | ${title}`);
    const dispatch = useDispatch();
    const { rows, loading, error, totalPages, total, from, to } = useSelector((state) => state.rewardLedger[stateKey]);
    const { submitting, activeId } = useSelector((state) => state.rewardLedgerAction);

    // Approve / Reject modal: { type: "approve" | "reject", entry } or null
    const [action, setAction] = useState(null);
    const [reason, setReason] = useState("");

    const [bill, setBill] = useState(null);
    const [billPreview, setBillPreview] = useState(null);

    const [draft, setDraft] = useState(defaultFilters);
    const [applied, setApplied] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);

    // Customer search (name / mobile / email) with suggestions
    const [customerQuery, setCustomerQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggesting, setSuggesting] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const customerBoxRef = useRef(null);
    const searchSeq = useRef(0);
    const keyword = customerQuery.trim();

    useEffect(() => {
        const seq = ++searchSeq.current; // invalidates any in-flight request
        if (draft.customerId || keyword.length < 3) {
            setSuggestions([]);
            setSuggesting(false);
            return;
        }
        setSuggesting(true);
        const t = setTimeout(async () => {
            try {
                const res = await getUserlist(10, 1, keyword);
                if (seq !== searchSeq.current) return;
                const list = res?.data?.data || res?.data || [];
                setSuggestions((Array.isArray(list) ? list : []).map(normalizeCustomer));
            } catch {
                if (seq === searchSeq.current) setSuggestions([]);
            } finally {
                if (seq === searchSeq.current) setSuggesting(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [keyword, draft.customerId]);

    useEffect(() => {
        const onDown = (ev) => {
            if (customerBoxRef.current && !customerBoxRef.current.contains(ev.target)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const handleCustomerInput = (e) => {
        setCustomerQuery(e.target.value);
        setDraft((prev) => ({ ...prev, customerId: "" })); // typing again un-selects
        setShowSuggestions(true);
    };

    const selectCustomer = (c) => {
        setDraft((prev) => ({ ...prev, customerId: c.id }));
        setCustomerQuery(c.name);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const clearCustomer = () => {
        setDraft((prev) => ({ ...prev, customerId: "" }));
        setCustomerQuery("");
        setSuggestions([]);
    };

    const load = () =>
        dispatch(fetchAction({ perPage: PER_PAGE, page: currentPage, filters: buildPayload(applied) }));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, fetchAction, currentPage, applied]);

    const handleDraft = (e) => setDraft((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) {
            toastr.error("From date cannot be after To date");
            return;
        }
        if (keyword && !draft.customerId) {
            toastr.error("Pick a customer from the suggestions, or clear the customer search");
            return;
        }
        setCurrentPage(1);
        setApplied({ ...draft });
    };

    const handleReset = () => {
        const fresh = defaultFilters();
        setDraft(fresh);
        setCustomerQuery("");
        setSuggestions([]);
        setCurrentPage(1);
        setApplied(fresh);
    };

    const entries = rows.map(normalizeLedgerRow);

   const resetBill = () => {
    if (billPreview) URL.revokeObjectURL(billPreview);
    setBill(null);
    setBillPreview(null);
    };

    const openAction = (type, entry) => {
        setReason("");
        resetBill();
        setAction({ type, entry });
    };

    const closeAction = () => {
        if (submitting) return;
        setAction(null);
        setReason("");
        resetBill();
    };

    const handleBillChange = (ev) => {
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        const file = ev.target.files?.[0];
        ev.target.value = "";
        if (!file) return;
        if (!BILL_TYPES.includes(file.type)) {
            toastr.error("Only PNG, JPG or PDF files are allowed");
            return;
        }
        if (file.size > BILL_MAX_BYTES) {
            toastr.error("File size must be 10 MB or less");
            return;
        }
        resetBill();
        setBill(file);
        setBillPreview(URL.createObjectURL(file));
    };

    const confirmAction = async () => {
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        const { type, entry } = action;

        if (type === "reject" && !reason.trim()) {
            toastr.error("Please enter a reason for rejection");
            return;
        }

        if (type === "approve" && !bill && !entry.billUrl) {
            toastr.error("Please upload the bill before approving");
            return;
        }

        const result =
            type === "approve"
                ? await dispatch(approvePendingReward({ ledgerId: entry.id, bill }))
                : await dispatch(rejectPendingReward({ ledgerId: entry.id, reason: reason.trim() }));

        if (result.meta.requestStatus === "fulfilled") {
            toastr.success(result.payload?.message);
            setAction(null);
            setReason("");
            resetBill();
            load(); // entry leaves the pending list
        } else {
            toastr.error(result.payload || "Something went wrong");
        }
    };

    return (
        <div className="w-full bg-white shadow-lg rounded-2xl p-6">
            <div className="mb-4">
                <h2 className="text-2xl font-bold">{title}</h2>
                {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>

            {/* Filters */}
            <form onSubmit={handleApply} className="mb-5 space-y-3">
                {/* Customer search */}
                <div ref={customerBoxRef} className="relative w-full">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
                    <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={customerQuery}
                            onChange={handleCustomerInput}
                            onFocus={() => setShowSuggestions(true)}
                            onKeyDown={(ev) => {
                                if (ev.key === "Enter" && keyword && !draft.customerId) ev.preventDefault();
                                if (ev.key === "Escape") setShowSuggestions(false);
                            }}
                            placeholder="Search by name, mobile or email (min 3 characters)"
                            autoComplete="off"
                            className={`${inputCls} pl-9 pr-9`}
                        />
                        {customerQuery && (
                            <button type="button" onClick={clearCustomer} title="Clear customer" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    {showSuggestions && !draft.customerId && keyword.length >= 3 && (
                        <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-64 overflow-auto">
                            {suggesting ? (
                                <li className="px-3 py-2 text-sm text-gray-500">Searching...</li>
                            ) : suggestions.length > 0 ? (
                                suggestions.map((c) => (
                                    <li
                                        key={c.id}
                                        onClick={() => selectCustomer(c)}
                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                        <div className="text-sm font-medium">{c.name}</div>
                                        <div className="text-xs text-gray-500">{[c.email, c.mobile].filter(Boolean).join(" | ")}</div>
                                    </li>
                                ))
                            ) : (
                                <li className="px-3 py-2 text-sm text-gray-500">No customers found</li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From date</label>
                    <input type="date" name="fromDate" value={draft.fromDate} onChange={handleDraft} className={inputCls} />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To date</label>
                    <input type="date" name="toDate" value={draft.toDate} min={draft.fromDate || undefined} onChange={handleDraft} className={inputCls} />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Booking / Reference</label>
                    <input type="text" name="bookingId" value={draft.bookingId} onChange={handleDraft} placeholder="Booking ID" className={inputCls} />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
                    <select name="mode" value={draft.mode} onChange={handleDraft} className={inputCls}>
                        <option value="">All modes</option>
                        {MODE_OPTIONS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-end gap-2">
                    <button type="submit" className="flex-1 inline-flex items-center justify-center gap-1 py-2 px-3 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors text-sm">
                        <Search size={16} /> Search
                    </button>
                    <button type="button" onClick={handleReset} title="Reset filters" className="py-2 px-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
                        <RotateCcw size={16} />
                    </button>
                </div>
                </div>
            </form>

            {error && (
                <div className="mb-4 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                    <span>{error}</span>
                    <button type="button" onClick={load} className="font-medium underline">Retry</button>
                </div>
            )}

            {/* Ledger */}
            <div className="overflow-auto border rounded-xl">
                <table className="w-full table-fixed text-sm text-left">
                    <thead className="text-gray-700 bg-gray-100">
                        <tr>
                            <th className="p-3 w-[26%]">Customer &amp; date</th>
                            <th className="p-3 w-[22%]">Reference &amp; description</th>
                            <th className="p-3 w-[15%]">Party</th>
                            <th className="p-3 w-[11%]">Type</th>
                            <th className="p-3 w-[15%]">Status
                            {stateKey === "pending" && " & Order Amount"}
                            </th>
                            <th className="p-3 w-[16%] text-right">Points &amp; balance points</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr>
                                <td className="p-3" colSpan={6}>
                                    <ShimmerLoader />
                                    <ShimmerLoader />
                                    <ShimmerLoader />
                                </td>
                            </tr>
                        ) : entries.length > 0 ? (
                            entries.map((e) => {
                                const tone = e.isCredit ? TONE.credit : TONE.debit;
                                const Icon = e.isCredit ? ArrowDownLeft : ArrowUpRight;
                                return (
                                    <tr key={e.id} className="hover:bg-gray-50 align-top">
                                        <td className={`p-3 break-words ${tone.edge}`}>
                                            <div className="font-medium">{e.customerName || "-"}</div>
                                            <div className="text-xs text-gray-500 break-all">{e.customerEmail}</div>
                                            <div className="text-xs text-gray-500">{e.customerMobile}</div>
                                            <div className="text-xs text-gray-700 mt-1">
                                                {e.createdAt ? dayjs(e.createdAt).format("DD MMM YYYY, hh:mm A") : "-"}
                                            </div>
                                        </td>
                                        <td className="p-3 break-words">
                                            <div className="font-medium break-all">{e.referenceNo || "-"}</div>
                                            <div className="text-xs text-gray-500">{e.description}</div>
                                        </td>
                                        <td className="p-3 break-words">
                                            <div>{e.from || "-"}</div>
                                            <ArrowRight size={14} className="text-gray-400 my-0.5" />
                                            <div>{e.to || "-"}</div>
                                        </td>
                                        <td className="p-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tone.badge}`}>
                                                <Icon size={12} /> {tone.label}
                                            </span>
                                            <div className="text-xs text-gray-500 mt-1">{e.mode || "-"}</div>
                                        </td>
                                        <td className="p-3">
                                        <div className="flex flex-col items-start gap-2">
                                            {/* Status */}
                                            <span
                                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                                    STATUS_STYLE[e.status] || STATUS_FALLBACK
                                                }`}
                                            >
                                                {e.status || "-"}
                                            </span>

                                            {/* Order Amount - only for Pending ledger */}
                                            {stateKey === "pending" && (
                                                <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-1 min-w-[110px]">
                                                    <div className="text-[9px] uppercase tracking-wide font-medium text-amber-600">
                                                        Order Amount
                                                    </div>

                                                    <div className="text-sm font-bold text-gray-800 mt-0.2">
                                                        ₹{fmt(e.order_amount)}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Approved at / by - pending ledger, once the entry is no longer PENDING */}
                                            {stateKey === "pending" && e.status !== "PENDING" && (
                                                <div className="text-xs text-gray-600 space-y-0.5">
                                                    <div>
                                                        <span className="text-gray-500">Approved at: </span>
                                                        {e.approvedAt ? dayjs(e.approvedAt).format("DD MMM YYYY, hh:mm A") : "-"}
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Approved by: </span>
                                                        <span className="font-medium">{e.approvedBy}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {e.billUrl && (
                                                <a
                                                    href={e.billUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                                                >
                                                    <Eye size={12} /> View Bill
                                                </a>
                                            )}

                                            {/* Approve / Reject - only while status is PENDING */}
                                            {stateKey === "pending" && e.status === "PENDING" && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("approve", e)}
                                                        disabled={submitting && activeId === e.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                                                    >
                                                        <Check size={12} /> Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("reject", e)}
                                                        disabled={submitting && activeId === e.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                                                    >
                                                        <X size={12} /> Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                        <td className="p-3 text-right">
                                            <div className={`font-semibold ${tone.amount}`}>{tone.sign}{fmt(e.amount)}</div>
                                            <div className="text-xs text-gray-500 mt-1">Bal. pts {fmt(e.balance)}</div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td className="p-6 text-center text-gray-500" colSpan={6}>{emptyText}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-3">
                <p className="text-xs text-gray-500">
                    {!loading && total > 0 ? `Showing ${from}–${to} of ${total}` : ""}
                </p>
                <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
            </div>

            {/* Approve / Reject confirmation modal */}
            {action && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onMouseDown={closeAction}>
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5" onMouseDown={(ev) => ev.stopPropagation()}>
                        <h3 className="text-lg font-semibold">
                            {action.type === "approve" ? "Approve reward" : "Reject reward"}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {action.entry.customerName || "Customer"} · {action.entry.referenceNo || `#${action.entry.id}`} ·{" "}
                            <span className="font-medium">{fmt(action.entry.amount)}</span>
                        </p>

                        {action.type === "approve" ? (
                        <div className="mt-3 space-y-3">
                            <p className="text-sm text-gray-700">Upload the bill, then approve this pending reward.</p>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Bill (PNG, JPG or PDF, max 10 MB) <span className="text-red-500">*</span>
                                </label>
                                <label className="flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed rounded-xl text-sm text-gray-600 cursor-pointer hover:bg-gray-50">
                                    <Upload size={16} />
                                    {bill ? "Change file" : action.entry.billUrl ? "Replace uploaded bill" : "Choose file"}
                                    <input
                                        type="file"
                                        accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                                        onChange={handleBillChange}
                                        className="hidden"
                                    />
                                </label>

                                {bill && (
                                    <div className="flex items-center justify-between mt-2 text-xs text-gray-700">
                                        <span className="truncate mr-2">{bill.name} ({(bill.size / 1024 / 1024).toFixed(2)} MB)</span>
                                        <a href={billPreview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline shrink-0">
                                            <Eye size={12} /> View
                                        </a>
                                    </div>
                                )}

                                {!bill && action.entry.billUrl && (
                                    <a href={action.entry.billUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:underline">
                                        <Eye size={12} /> View uploaded bill
                                    </a>
                                )}
                                </div>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Reason for rejection <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(ev) => setReason(ev.target.value)}
                                    placeholder="Enter the reason"
                                    autoFocus
                                    className={inputCls}
                                />
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                type="button"
                                onClick={closeAction}
                                disabled={submitting}
                                className="px-4 py-2 border rounded-xl text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmAction}
                                disabled={submitting}
                                className={`px-4 py-2 rounded-xl text-sm text-white disabled:opacity-50 ${
                                    action.type === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                                }`}
                            >
                                {submitting ? "Please wait..." : action.type === "approve" ? "Approve" : "Reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}